import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { i18n } from '~/utils';

import {
  type ParsingCaseDraftResponse,
  type SaveParsingCaseDraftVariables,
} from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { saveParsingCaseDraft } from './saveParsingCaseDraft';

/**
 * Custom hook to save a parsing case draft without retrying the case.
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/put_parsing_case_draft_vadmin_parsing_cases__case_id__draft_put
 */
export const useMutationSaveParsingCaseDraft = (
  options?: UseMutationOptions<
    ParsingCaseDraftResponse,
    unknown,
    SaveParsingCaseDraftVariables
  >,
) => {
  const actionOptions =
    useParsingCaseActionMutationOptions<SaveParsingCaseDraftVariables>(() =>
      i18n.t(($) => $.saveParsingCaseDraft.success, { ns: 'data' }),
    );

  return useMutation<
    ParsingCaseDraftResponse,
    unknown,
    SaveParsingCaseDraftVariables
  >({
    mutationFn: async (variables) => saveParsingCaseDraft(variables),
    ...actionOptions,
    ...options,
  });
};
